// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

/**
 * Sync client — talks to the hushold sync server.
 *
 * API contract (from packages/sync-server/src/):
 *   POST /auth/register  { username, serverAuthToken } -> { token, refreshToken, expiresIn, userId }
 *   POST /auth/login     { username, serverAuthToken } -> { token, refreshToken, expiresIn, userId }
 *   POST /auth/refresh   { refreshToken } -> { token, refreshToken, expiresIn, userId }
 *   POST /auth/logout    { refreshToken } -> { ok }
 *   GET  /auth/keys      (auth)  -> { bundle: KeyMaterialBundle }
 *   POST /auth/keys      (auth)  { bundle, recoveryProof } -> 201
 *   PUT  /auth/password  (auth)  { oldServerAuthToken, newServerAuthToken, newBundle } -> ok
 *   POST /auth/recover   { username, recoveryProof, newServerAuthToken, newBundle, newRecoveryProof } -> { token, refreshToken }
 *   GET  /sync/status    (auth)  -> { lastSequence, blobCount }
 *   GET  /sync/log?since=N (auth) -> { blobs: BlobEntry[] }
 *   POST /sync/push      (auth) { data: base64 } -> { sequence, blobHash }
 *   POST /sync/rekey     (auth) { newBundle, blobs: base64[] } -> { lastSequence }
 *   DELETE /account      (auth) -> 204
 */

import type { BlobEntry, LedgerEvent, KeyMaterialBundle } from '../types.js';

export interface SyncClientConfig {
  baseUrl: string;
  token: string;
}

export interface SessionTokens {
  token: string;
  refreshToken: string;
  expiresIn: number;
  userId: string;
}

export type RegisterResult = SessionTokens;
export type LoginResult = SessionTokens;

export interface SyncStatusResult {
  lastSequence: number;
  blobCount: number;
}

export interface PushResult {
  sequence: number;
  blobHash: string;
}

async function fetchJson<T>(
  url: string,
  options: RequestInit,
): Promise<T> {
  const res = await fetch(url, options);
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }
  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export async function registerUser(
  baseUrl: string,
  username: string,
  serverAuthToken: string,
): Promise<RegisterResult> {
  return fetchJson<RegisterResult>(`${baseUrl}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, serverAuthToken }),
  });
}

export async function loginUser(
  baseUrl: string,
  username: string,
  serverAuthToken: string,
): Promise<LoginResult> {
  return fetchJson<LoginResult>(`${baseUrl}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, serverAuthToken }),
  });
}

/** Exchange a refresh token for a new access token (rotates the refresh token). */
export async function refreshSession(
  baseUrl: string,
  refreshToken: string,
): Promise<SessionTokens> {
  return fetchJson<SessionTokens>(`${baseUrl}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
}

/** Revoke a refresh token server-side (best-effort; safe to ignore failures). */
export async function logoutSession(baseUrl: string, refreshToken: string): Promise<void> {
  try {
    await fetchJson<void>(`${baseUrl}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    /* logout is best-effort; the token expires on its own regardless */
  }
}

export async function getKeyBundle(config: SyncClientConfig): Promise<KeyMaterialBundle> {
  const result = await fetchJson<{ bundle: KeyMaterialBundle }>(
    `${config.baseUrl}/auth/keys`,
    { headers: { Authorization: `Bearer ${config.token}` } },
  );
  return result.bundle;
}

export async function postKeyBundle(
  config: SyncClientConfig,
  bundle: KeyMaterialBundle,
  recoveryProof: string,
): Promise<void> {
  return fetchJson<void>(`${config.baseUrl}/auth/keys`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${config.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ bundle, recoveryProof }),
  });
}

export async function changePassword(
  config: SyncClientConfig,
  oldServerAuthToken: string,
  newServerAuthToken: string,
  newBundle: KeyMaterialBundle,
): Promise<void> {
  return fetchJson<void>(`${config.baseUrl}/auth/password`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${config.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ oldServerAuthToken, newServerAuthToken, newBundle }),
  });
}

/** Fetch the key-material bundle during recovery, gated by the recovery proof. */
export async function getRecoverKeys(
  baseUrl: string,
  username: string,
  recoveryProof: string,
): Promise<KeyMaterialBundle> {
  const result = await fetchJson<{ bundle: KeyMaterialBundle }>(`${baseUrl}/auth/recover/keys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, recoveryProof }),
  });
  return result.bundle;
}

export async function recoverAccount(
  baseUrl: string,
  username: string,
  recoveryProof: string,
  newServerAuthToken: string,
  newBundle: KeyMaterialBundle,
  newRecoveryProof: string,
): Promise<LoginResult> {
  return fetchJson<LoginResult>(`${baseUrl}/auth/recover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, recoveryProof, newServerAuthToken, newBundle, newRecoveryProof }),
  });
}

/**
 * Rotate the ledger encryption key (SEC-07): atomically replace the entire
 * encrypted log with `blobs` (re-encrypted under a new DEK) and swap the stored
 * key-material bundle. Returns the new last sequence.
 */
export async function rekeyLog(
  config: SyncClientConfig,
  newBundle: KeyMaterialBundle,
  blobs: Uint8Array[],
): Promise<{ lastSequence: number }> {
  const encoded = blobs.map((b) => encodeBlob(b));
  return fetchJson<{ lastSequence: number }>(`${config.baseUrl}/sync/rekey`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${config.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ newBundle, blobs: encoded }),
  });
}

export async function getSyncStatus(config: SyncClientConfig): Promise<SyncStatusResult> {
  return fetchJson<SyncStatusResult>(`${config.baseUrl}/sync/status`, {
    headers: { Authorization: `Bearer ${config.token}` },
  });
}

export async function fetchSyncLog(
  config: SyncClientConfig,
  since = 0,
): Promise<BlobEntry[]> {
  const result = await fetchJson<{ blobs: BlobEntry[] }>(
    `${config.baseUrl}/sync/log?since=${since}`,
    { headers: { Authorization: `Bearer ${config.token}` } },
  );
  return result.blobs;
}

/**
 * Push an encrypted event blob to the server.
 * The blob must be a Uint8Array in the wire format:
 *   salt(32) + iv(12) + authTag(16) + ciphertext
 */
export async function pushBlob(
  config: SyncClientConfig,
  blob: Uint8Array,
): Promise<PushResult> {
  const base64 = encodeBlob(blob);
  return fetchJson<PushResult>(`${config.baseUrl}/sync/push`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${config.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data: base64 }),
  });
}

/**
 * Base64-encode a blob for the wire. Encodes in 32 KiB chunks rather than
 * `btoa(String.fromCharCode(...blob))`: spreading a large Uint8Array into
 * String.fromCharCode passes one argument per byte, and once the blob exceeds
 * the engine's argument-count limit V8 throws "Maximum call stack size
 * exceeded". A real GnuCash import easily produces a blob that large.
 */
export function encodeBlob(blob: Uint8Array): string {
  const CHUNK = 0x8000; // 32 KiB — comfortably under the arg-count limit
  let binary = '';
  for (let i = 0; i < blob.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, blob.subarray(i, i + CHUNK) as unknown as number[]);
  }
  return btoa(binary);
}

/** Decode a base64 blob from the server response into Uint8Array. */
export function decodeBlob(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Delete the account (GDPR right to erasure). */
export async function deleteAccount(config: SyncClientConfig): Promise<void> {
  return fetchJson<void>(`${config.baseUrl}/account`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${config.token}` },
  });
}

/**
 * Decrypt blobs using the crypto worker.
 * Returns a flat list of LedgerEvent[] in sequence order.
 */
export async function decryptBlobs(
  blobs: BlobEntry[],
  worker: Worker,
  keyData: ArrayBuffer,
): Promise<LedgerEvent[]> {
  if (blobs.length === 0) return [];

  const allEvents: LedgerEvent[] = [];

  for (const blob of blobs) {
    if (!blob.data) continue;

    const bytes = decodeBlob(blob.data);
    const events = await new Promise<LedgerEvent[]>((resolve, reject) => {
      const id = `decrypt-${blob.sequence}-${Date.now()}`;
      const handler = (e: MessageEvent) => {
        const msg = e.data as { type: string; id: string; events?: LedgerEvent[]; message?: string };
        if (msg.id !== id) return;
        worker.removeEventListener('message', handler);
        if (msg.type === 'DECRYPT_RESULT' && msg.events) {
          resolve(msg.events);
        } else if (msg.type === 'ERROR') {
          reject(new Error(msg.message ?? 'Decryption failed'));
        }
      };
      worker.addEventListener('message', handler);
      worker.postMessage({ type: 'DECRYPT', id, blob: Array.from(bytes), keyData });
    });

    allEvents.push(...events);
  }

  return allEvents;
}
